import React, { useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { InfoCircleFilled, CaretUpOutlined, CaretDownOutlined } from '@ant-design/icons';
import { Tooltip, Spin } from 'antd';
import { ASC, DESC } from '@utils/table/constants';
import dimensionsSelector from '@store/common/dimensionList/selectors';
import { useViewerStore, actions } from '@store/viewerModule/base';
import vmSelector from '@store/viewerModule/selectors';
import { videoViewerModuleAISummary, featureSelector } from '@store/common/selectors';
import Empty from '@components/viewer-sessions/table/Empty';
import { AnchoredTable } from '@src/components/common/Table';
import generateUUid from '@utils/generateUUID';
import {
  filterByColumns,
  sortTable,
  replaceFinalDataKey,
  replaceKey,
  getColumnsNameReflection,
  GROUPBY_MAPPING,
} from './utils';
import ColumnDropMenu from './ColumnsDropMenu';
import Search from './Search';
import useFetchData from './useFetchData';
import { getCellRenderer } from './columns';
import {
  ViewerSessionsTableHeader,
  DataTriggerBoxWrapper,
  DataTableWrapper,
  DataTriggerBox,
  Styled_ViewerTableTitle,
  Styled_IconWrapper,
  SortButton,
  HeaderActions,
  LoadMoreFooter,
} from './styled';
import { useAtomValue } from 'jotai';
import { alertsSessionAssociation, alertList } from '@components/viewer-module/AISummary/atom';

const mapDimensionGroupBy = (dimension) => {
  return get(GROUPBY_MAPPING, dimension.groupBy, dimension.groupBy);
};

const AI_ALERT_COLUMN_METRIC = 'Alert';

// This is used for displaying the association between sessions and alerts
const AI_ALERT_COLUMN = {
  metric: AI_ALERT_COLUMN_METRIC,
  show: true,
};

const endedPlayText =
  'Ended Session value “No” indicates that the session is still active and end time is not yet recorded. Ended Session is different from Ended Plays metric. Check Learning Centre for more details.';

function SubscriberTable({ sessionData }) {
  const [selectedRowIndex, setRowIndex] = useState(-1);
  const [selectRowIndex, setSelectRowIndex] = useState();
  const [isSelected, setIsSelected] = useState(false);
  const [columnSearchTexts, setColumnSearchTexts] = useState<
    Record<string, { value: string; operator: 'contains' | 'not_contains' }>
  >({});
  const { fetchNextPage, isFetchingNextPage, hasNextPage } = useFetchData();
  const [{ searchSortData }, dispatchContext] = useViewerStore();

  const handleColumnSearch = useCallback(
    (dataKey: string, value: string, operator: 'contains' | 'not_contains' = 'contains') => {
      setColumnSearchTexts((prev) => ({ ...prev, [dataKey]: { value, operator } }));
    },
    [],
  );

  const alertsSessionAssociationValue = useAtomValue(alertsSessionAssociation);
  const alertListValue = useAtomValue(alertList);

  const showPromptAISummary = useSelector(videoViewerModuleAISummary);
  const aiAlertEnabled = useSelector(featureSelector('ai_alerts'));

  const store = useSelector(vmSelector);
  const [vmStore] = useViewerStore();
  const { response: dimensions } = useSelector(dimensionsSelector);
  const { anchoredColumns } = vmStore;

  const tableColumns = React.useMemo(() => {
    const columnsNameReflections = {
      ...getColumnsNameReflection(),
      ...Object.fromEntries(dimensions.map((d) => [mapDimensionGroupBy(d), d.name])),
      Alert: AI_ALERT_COLUMN_METRIC,
    };

    const allTableColumns =
      showPromptAISummary && aiAlertEnabled
        ? [AI_ALERT_COLUMN, ...store.base.selectedColumns]
        : store.base.selectedColumns;

    return allTableColumns
      .filter((item) => item.show)
      .map((item) => ({
        dataKey: replaceKey(item.metric),
        frozen: anchoredColumns[item.metric] ? 'left' : false,
        title: columnsNameReflections[item.metric],
        width: item.metric !== AI_ALERT_COLUMN_METRIC ? 200 : 100,
        height: 60,
        resizable: true,
        sortable: false,
        maxWidth: 340,
        minWidth: 60,
        key: generateUUid(),
        headerRenderer: ({ column }) => {
          const showActions = item.metric !== AI_ALERT_COLUMN_METRIC;
          const isActiveSort = searchSortData.columnOrderKey === column.dataKey;
          const currentOrder = isActiveSort ? searchSortData.order : null;

          const handleSort = (e) => {
            e.stopPropagation();
            const nextOrder = currentOrder === ASC ? DESC : ASC;
            columnSort({
              key: column.key,
              order: nextOrder,
              column: { dataKey: column.dataKey },
            });
          };

          return (
            <Styled_ViewerTableTitle>
              <span>{column.title}</span>
              {column.dataKey === 'IsEnded' && (
                <Tooltip placement="top" title={endedPlayText}>
                  <Styled_IconWrapper>
                    <InfoCircleFilled />
                  </Styled_IconWrapper>
                </Tooltip>
              )}
              {showActions && (
                <HeaderActions>
                  <SortButton $active={isActiveSort} onClick={handleSort}>
                    <CaretUpOutlined
                      style={{ color: currentOrder === ASC ? '#1890ff' : undefined }}
                    />
                    <CaretDownOutlined
                      style={{ color: currentOrder === DESC ? '#1890ff' : undefined }}
                    />
                  </SortButton>
                  <Search
                    onSearch={(value, operator) =>
                      handleColumnSearch(column.dataKey, value, operator)
                    }
                  />
                </HeaderActions>
              )}
            </Styled_ViewerTableTitle>
          );
        },
        cellRenderer: ({ column, rowData, cellData }) =>
          getCellRenderer(
            item.metric,
            column,
            rowData,
            cellData,
            alertsSessionAssociationValue,
            alertListValue,
          ),
      }));
  }, [
    anchoredColumns,
    store.base.selectedColumns,
    dimensions,
    showPromptAISummary,
    alertsSessionAssociationValue,
    alertListValue,
    handleColumnSearch,
    searchSortData,
  ]);

  const headerRender = useCallback(
    ({ cells, columns }) =>
      columns.map((item, index) => {
        return (
          <ViewerSessionsTableHeader key={item.title} title={item.title}>
            {cells[index]}
          </ViewerSessionsTableHeader>
        );
      }),
    [],
  );

  const columnSort = (data) => {
    const {
      key,
      order: newOrder,
      column: { dataKey },
    } = data;
    dispatchContext(
      actions.setSearchSortData({
        order: newOrder,
        orderKey: key,
        columnOrderKey: dataKey,
      }),
    );
  };

  // Data pipeline: replace keys → filter by search → sort
  const replacedData = useMemo(
    () => (sessionData ? replaceFinalDataKey(sessionData) : []),
    [sessionData],
  );

  const filteredData = useMemo(
    () => filterByColumns(replacedData, columnSearchTexts),
    [replacedData, columnSearchTexts],
  );

  const tableData = useMemo(() => {
    const { order, columnOrderKey } = searchSortData;
    return sortTable(order, filteredData, columnOrderKey);
  }, [filteredData, searchSortData]);

  const onRowClick = (row) => {
    const { rowData, rowIndex } = row;
    setSelectRowIndex(rowIndex);
    if (rowIndex !== selectedRowIndex && !isSelected) {
      setRowIndex(rowIndex);
      setIsSelected(true);
    } else {
      setRowIndex(-1);
      setIsSelected(false);
    }
    const geoData: any = {
      startTime: rowData.SessionStartTime,
      encodedGeo: rowData.location ? rowData.location.encodedGeo : '',
      asn: rowData.location ? rowData.location.asn : '',
      city: rowData.location ? rowData.location.cityCode : '',
    };
    dispatchContext(actions.setAdvanced({ showAdvanceDetails: true }));
    dispatchContext(actions.setRowData({ rowData }));
    dispatchContext(actions.setGeoData({ geoData }));
  };

  const rowEventHandlers = {
    onClick: onRowClick,
  };

  const rowClassName = (row) => {
    const { rowIndex } = row;
    const isCurrentRowSelected = rowIndex === selectRowIndex;
    const className = isCurrentRowSelected ? 'row-selected' : '';
    return className;
  };

  const loadMoreFooter = () => (
    <LoadMoreFooter onClick={() => fetchNextPage()}>
      {isFetchingNextPage ? <Spin size="small" /> : 'Load More'}
    </LoadMoreFooter>
  );

  return (
    <>
      <DataTriggerBoxWrapper>
        <DataTriggerBox $pushRight={false}>
          <ColumnDropMenu />
        </DataTriggerBox>
      </DataTriggerBoxWrapper>
      <DataTableWrapper>
        <AnchoredTable
          fixed
          data={tableData}
          columns={tableColumns}
          headerHeight={45}
          rowHeight={40}
          headerRenderer={headerRender}
          rowEventHandlers={rowEventHandlers}
          rowClassName={rowClassName}
          emptyRenderer={<Empty />}
          footerHeight={hasNextPage ? 40 : 0}
          footerRenderer={hasNextPage ? loadMoreFooter : null}
        />
      </DataTableWrapper>
    </>
  );
}

export default React.memo(SubscriberTable);
